import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  alpha,
  useTheme,
  Fade,
  Zoom
} from '@mui/material';
import {
  Lock,
  Save,
  Visibility,
  VisibilityOff,
  Person,
  Security,
  CheckCircle,
  Shield,
  Key,
  Badge
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Glass Card Component
const GlassCard = ({ children, sx = {}, ...props }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 4,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        ...sx
      }}
      {...props}
    >
      {children}
    </Paper>
  );
};

// Gradient Button Component
const GradientButton = ({ children, variant = 'primary', sx = {}, ...props }) => {
  const gradients = {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    success: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  };

  return (
    <Button
      sx={{
        background: gradients[variant],
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        px: 4,
        py: 1.5,
        borderRadius: 3,
        boxShadow: `0 4px 15px ${alpha('#667eea', 0.4)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          background: gradients[variant],
          transform: 'translateY(-2px)',
          boxShadow: `0 6px 20px ${alpha('#667eea', 0.6)}`,
        },
        '&:disabled': {
          background: 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)',
          boxShadow: 'none',
        },
        ...sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default function SettingsPanel({ user }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/change-password`,
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        },
        { withCredentials: true }
      );

      setMessage({ type: 'success', text: response.data.message || 'Password changed successfully' });
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to change password'
      });
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Fade in timeout={500}>
      <Box>
        <Grid container spacing={4}>
          {/* Password Change Section */}
          <Grid item xs={12} md={7}>
            <Zoom in timeout={300}>
              <Box>
                <GlassCard>
                  {/* Header */}
                  <Box sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                      }}>
                        <Security sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Security Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Update your password to keep your account secure
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Content */}
                  <Box sx={{ p: 4 }}>
                    {message.text && (
                      <Alert
                        severity={message.type}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          '& .MuiAlert-icon': {
                            alignItems: 'center'
                          }
                        }}
                        onClose={() => setMessage({ type: '', text: '' })}
                        icon={message.type === 'success' ? <CheckCircle /> : undefined}
                      >
                        {message.text}
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Current Password */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 1,
                            display: 'block'
                          }}>
                            Current Password
                          </Typography>
                          <TextField
                            fullWidth
                            type={showPasswords.current ? 'text' : 'password'}
                            name="currentPassword"
                            placeholder="Enter your current password"
                            value={formData.currentPassword}
                            onChange={handleChange}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword}
                            autoComplete="current-password"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => togglePasswordVisibility('current')}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                background: alpha(theme.palette.action.hover, 0.3)
                              }
                            }}
                          />
                        </Box>

                        <Divider sx={{ my: 1 }}>
                          <Chip
                            label="New Password"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              background: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main
                            }}
                          />
                        </Divider>

                        {/* New Password */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 1,
                            display: 'block'
                          }}>
                            New Password
                          </Typography>
                          <TextField
                            fullWidth
                            type={showPasswords.new ? 'text' : 'password'}
                            name="newPassword"
                            placeholder="Enter your new password"
                            value={formData.newPassword}
                            onChange={handleChange}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword || 'Minimum 6 characters'}
                            autoComplete="new-password"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Key sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => togglePasswordVisibility('new')}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                background: alpha(theme.palette.action.hover, 0.3)
                              }
                            }}
                          />
                        </Box>

                        {/* Confirm Password */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 1,
                            display: 'block'
                          }}>
                            Confirm New Password
                          </Typography>
                          <TextField
                            fullWidth
                            type={showPasswords.confirm ? 'text' : 'password'}
                            name="confirmPassword"
                            placeholder="Confirm your new password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            autoComplete="new-password"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Key sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => togglePasswordVisibility('confirm')}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                background: alpha(theme.palette.action.hover, 0.3)
                              }
                            }}
                          />
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <GradientButton
                            type="submit"
                            fullWidth
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Save />}
                          >
                            {loading ? 'Updating Password...' : 'Update Password'}
                          </GradientButton>
                        </Box>
                      </Box>
                    </form>
                  </Box>
                </GlassCard>
              </Box>
            </Zoom>
          </Grid>

          {/* Account Info Section */}
          <Grid item xs={12} md={5}>
            <Zoom in timeout={400}>
              <Box>
                <GlassCard sx={{ height: '100%' }}>
                  {/* Header */}
                  <Box sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%)',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        boxShadow: '0 4px 12px rgba(56, 239, 125, 0.3)'
                      }}>
                        <Person sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Account Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Your account information
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Content */}
                  <Box sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Username Card */}
                      <Box sx={{
                        p: 3,
                        borderRadius: 3,
                        background: alpha(theme.palette.action.hover, 0.3),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: alpha(theme.palette.primary.main, 0.1)
                          }}>
                            <Badge sx={{ color: theme.palette.primary.main }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}>
                              Username
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {user?.username || 'N/A'}
                            </Typography>
                          </Box>
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 16 }} />}
                            label="Active"
                            size="small"
                            sx={{
                              background: alpha(theme.palette.success.main, 0.1),
                              color: theme.palette.success.main,
                              fontWeight: 600,
                              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                            }}
                          />
                        </Box>
                      </Box>

                      {/* User ID Card */}
                      <Box sx={{
                        p: 3,
                        borderRadius: 3,
                        background: alpha(theme.palette.action.hover, 0.3),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: alpha(theme.palette.secondary.main, 0.1)
                          }}>
                            <Shield sx={{ color: theme.palette.secondary.main }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}>
                              User ID
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace', mt: 0.5 }}>
                              #{user?.id || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Security Tips */}
                      <Box sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          Security Tips
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
                          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                            Use a unique password for this account
                          </Typography>
                          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                            Change your password regularly
                          </Typography>
                          <Typography component="li" variant="body2">
                            Never share your credentials
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </GlassCard>
              </Box>
            </Zoom>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
}
